import {
  DocumentNode,
  FieldNode,
  FragmentDefinitionNode,
  InlineFragmentNode,
  Kind,
  OperationDefinitionNode,
  SelectionNode,
  SelectionSetNode,
  visit,
} from 'graphql'

export const PARALLEL_DIRECTIVE = 'parallel'

export interface SplitParallelDocumentResult {
  /** Original document with every `... @parallel` inline fragment removed */
  main: DocumentNode
  /**
   * Companion documents fetching only the deferred selections (plus the
   * ancestor path and `id` merge keys) — one per distinct `group` argument,
   * annotations without a group sharing a single default document. Empty
   * when the document contains no `@parallel` directive.
   */
  deferred: DocumentNode[]
}

// Annotations without a group argument all share this bucket, so a document
// with only unkeyed `@parallel` fragments still produces a single companion
// query
const DEFAULT_GROUP = ''

// Codegen'd apollo-angular services hold a stable DocumentNode instance, so a
// reference-keyed WeakMap memoizes the split across every fetch()/reload()
const splitCache = new WeakMap<DocumentNode, SplitParallelDocumentResult>()

/**
 * Splits a GraphQL document annotated with `... @parallel(group: "…") { … }`
 * inline fragments into server-valid documents: `main` (everything except the
 * annotated selections) and one deferred companion per group (the annotated
 * selections, their ancestor fields with arguments, and the `id` sibling used
 * as merge key). All documents are fired in parallel by the state layer;
 * distinct groups land and merge independently.
 *
 * No output document contains the `@parallel` directive — it is a purely
 * client-side annotation the server must never see.
 *
 * v1 restrictions (loud dev-time errors):
 * - `@parallel` fragments must sit in a selection set that also selects `id`
 *   (entities are merged back by id, shallowly per entity)
 * - `@parallel` is not supported inside named fragment definitions
 */
export function splitParallelDocument(
  doc: DocumentNode
): SplitParallelDocumentResult {
  const cached = splitCache.get(doc)
  if (cached) {
    return cached
  }
  const result = split(doc)
  splitCache.set(doc, result)
  return result
}

function split(doc: DocumentNode): SplitParallelDocumentResult {
  const groups = collectGroups(doc)
  if (!groups.length) {
    return { main: doc, deferred: [] }
  }

  doc.definitions.forEach((definition) => {
    if (
      definition.kind === Kind.FRAGMENT_DEFINITION &&
      collectGroups(definition).length
    ) {
      throw new Error(
        `@${PARALLEL_DIRECTIVE} is not supported inside the named fragment ` +
          `"${definition.name.value}" — move the annotation into the ` +
          `operation's selection set`
      )
    }
  })

  return {
    main: buildMainDocument(doc),
    deferred: groups.map((group) => buildDeferredDocument(doc, group)),
  }
}

/** Distinct group keys in annotation order; unkeyed fragments map to one shared default group */
function collectGroups(node: DocumentNode | FragmentDefinitionNode): string[] {
  const groups: string[] = []
  visit(node, {
    InlineFragment(fragment) {
      const group = parallelGroup(fragment)
      if (group !== null && !groups.includes(group)) {
        groups.push(group)
      }
    },
  })
  return groups
}

/** The fragment's group key, or null when it carries no `@parallel` directive */
function parallelGroup(fragment: InlineFragmentNode): string | null {
  const directive = fragment.directives?.find(
    (candidate) => candidate.name.value === PARALLEL_DIRECTIVE
  )
  if (!directive) {
    return null
  }
  const groupArgument = directive.arguments?.find(
    (argument) => argument.name.value === 'group'
  )
  if (!groupArgument) {
    return DEFAULT_GROUP
  }
  if (groupArgument.value.kind !== Kind.STRING) {
    throw new Error(
      `@${PARALLEL_DIRECTIVE}(group:) expects a literal string, got ` +
        `${groupArgument.value.kind}`
    )
  }
  return groupArgument.value.value
}

/** The original document minus every `@parallel` inline fragment */
function buildMainDocument(doc: DocumentNode): DocumentNode {
  const withoutDeferred = visit(doc, {
    InlineFragment(fragment) {
      return parallelGroup(fragment) !== null ? null : undefined
    },
  })
  return pruneDocument(withoutDeferred)
}

/**
 * A document keeping only the ancestors of the given group's `@parallel`
 * fragments (with their arguments, so filters/pagination/order are identical
 * to the main query), the `id` merge keys along that path, and the annotated
 * selections with the directive stripped
 */
function buildDeferredDocument(doc: DocumentNode, group: string): DocumentNode {
  const definitions = doc.definitions.map((definition) => {
    if (definition.kind !== Kind.OPERATION_DEFINITION) {
      return definition
    }
    const selections = extractGroupSelections(definition.selectionSet, group)
    if (!selections) {
      return definition
    }
    const deferredOperation: OperationDefinitionNode = {
      ...definition,
      name: definition.name
        ? {
            ...definition.name,
            value: `${definition.name.value}Parallel${pascalCase(group)}`,
          }
        : definition.name,
      selectionSet: { kind: Kind.SELECTION_SET, selections },
    }
    return deferredOperation
  })
  return pruneDocument({ ...doc, definitions })
}

/** "digest-links" → "DigestLinks"; the default group maps to an empty suffix */
function pascalCase(group: string): string {
  return group
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')
}

/**
 * Recursively keeps only the selections leading to (or part of) a `@parallel`
 * fragment of the given group. Returns null when the subtree contains none.
 */
function extractGroupSelections(
  selectionSet: SelectionSetNode,
  group: string
): readonly SelectionNode[] | null {
  const idField = selectionSet.selections.find(
    (selection): selection is FieldNode =>
      selection.kind === Kind.FIELD &&
      selection.name.value === 'id' &&
      !selection.alias
  )
  const kept: SelectionNode[] = []
  for (const selection of selectionSet.selections) {
    if (
      selection.kind === Kind.INLINE_FRAGMENT &&
      parallelGroup(selection) !== null
    ) {
      if (parallelGroup(selection) !== group) {
        continue
      }
      if (!idField) {
        throw new Error(
          `@${PARALLEL_DIRECTIVE} requires an "id" sibling in the same ` +
            `selection set — entities are merged back by id. Move the ` +
            `annotation to the entity-node level (a selection set ` +
            `containing "id") and wrap whole top-level fields.`
        )
      }
      kept.push(stripParallel(selection))
    } else if (selection.kind === Kind.FIELD && selection.selectionSet) {
      const sub = extractGroupSelections(selection.selectionSet, group)
      if (sub) {
        kept.push({
          ...selection,
          selectionSet: { kind: Kind.SELECTION_SET, selections: sub },
        })
      }
    } else if (selection.kind === Kind.INLINE_FRAGMENT) {
      const sub = extractGroupSelections(selection.selectionSet, group)
      if (sub) {
        kept.push({
          ...selection,
          selectionSet: { kind: Kind.SELECTION_SET, selections: sub },
        })
      }
    }
    // FragmentSpread: named fragments cannot contain @parallel (checked
    // upfront), so a spread never leads to an annotated selection
  }
  if (!kept.length) {
    return null
  }
  if (idField) {
    kept.unshift(idField)
  }
  return kept
}

function stripParallel(fragment: InlineFragmentNode): InlineFragmentNode {
  return {
    ...fragment,
    directives: fragment.directives?.filter(
      (directive) => directive.name.value !== PARALLEL_DIRECTIVE
    ),
  }
}

/**
 * Removes fragment definitions and operation variable definitions that are no
 * longer referenced — both would fail server-side validation ("fragment is
 * never used" / "variable is never used") after a split.
 */
function pruneDocument(doc: DocumentNode): DocumentNode {
  const fragmentsByName = new Map<string, FragmentDefinitionNode>()
  doc.definitions.forEach((definition) => {
    if (definition.kind === Kind.FRAGMENT_DEFINITION) {
      fragmentsByName.set(definition.name.value, definition)
    }
  })

  const usedFragments = new Set<string>()
  const collectSpreads = (
    node: OperationDefinitionNode | FragmentDefinitionNode
  ): void => {
    visit(node, {
      FragmentSpread(spread) {
        const name = spread.name.value
        if (!usedFragments.has(name)) {
          usedFragments.add(name)
          const fragment = fragmentsByName.get(name)
          if (fragment) {
            collectSpreads(fragment)
          }
        }
      },
    })
  }

  const operations = doc.definitions.filter(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === Kind.OPERATION_DEFINITION
  )
  operations.forEach(collectSpreads)

  // Only usages inside selection sets count — visiting the operation itself
  // would see its own variable *definitions* and mark everything as used
  const usedVariables = new Set<string>()
  const collectVariables = (
    node: SelectionSetNode | FragmentDefinitionNode
  ): void => {
    visit(node, {
      Variable(variable) {
        usedVariables.add(variable.name.value)
      },
    })
  }
  operations.forEach((operation) => collectVariables(operation.selectionSet))
  usedFragments.forEach((name) => {
    const fragment = fragmentsByName.get(name)
    if (fragment) {
      collectVariables(fragment)
    }
  })

  const definitions = doc.definitions
    .filter(
      (definition) =>
        definition.kind !== Kind.FRAGMENT_DEFINITION ||
        usedFragments.has(definition.name.value)
    )
    .map((definition) => {
      if (definition.kind !== Kind.OPERATION_DEFINITION) {
        return definition
      }
      return {
        ...definition,
        variableDefinitions: definition.variableDefinitions?.filter(
          (variableDefinition) =>
            usedVariables.has(variableDefinition.variable.name.value)
        ),
      }
    })
  return { ...doc, definitions }
}
